import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react'
import type { Entry } from '@/types/domain'
import { formatBRL } from '../format'
import { Card, CardContent, CardHeader } from '../shadcn/card'
import { EntryRow } from './EntryRow'

export type SortColumn = 'numero' | 'data'
export type SortDirection = 'asc' | 'desc'

export interface SortState {
  column: SortColumn
  direction: SortDirection
}

export interface SectionProps {
  titulo: string
  totalBRL: number
  entries: Entry[]
  /** Stable display number per entry id, computed at the year-base level. */
  numeroOf: (entryId: string) => number
  sort: SortState
  onSort: (column: SortColumn) => void
  showTipo?: boolean
  emptyMessage?: string
  onEdit?: (entry: Entry) => void
  onCopy?: (entry: Entry) => void
  onRecalc?: (entry: Entry) => void
  onDelete?: (entry: Entry) => void
}

export function Section({
  titulo,
  totalBRL,
  entries,
  numeroOf,
  sort,
  onSort,
  showTipo,
  emptyMessage,
  onEdit,
  onCopy,
  onRecalc,
  onDelete,
}: SectionProps) {
  return (
    <Card className="overflow-hidden">
      <CardHeader className="flex flex-row items-center justify-between border-b border-border bg-muted/20 py-4 space-y-0">
        <h2 className="font-display text-xl tracking-tight font-normal leading-none">
          {titulo}
        </h2>
        <div className="text-right">
          <div className="text-muted-foreground text-[10px] tracking-widest2 uppercase">
            Total no período
          </div>
          <div className="font-mono text-xl font-medium">R$ {formatBRL(totalBRL)}</div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        <RowHeader showTipo={showTipo} sort={sort} onSort={onSort} />
        {entries.length === 0 ? (
          <p className="text-muted-foreground px-6 py-8 text-center text-sm italic">
            {emptyMessage ?? 'nenhum lançamento desta natureza no período'}
          </p>
        ) : (
          <div>
            {entries.map((entry) => (
              <EntryRow
                key={entry.id}
                entry={entry}
                numero={numeroOf(entry.id)}
                showTipo={showTipo}
                onEdit={onEdit}
                onCopy={onCopy}
                onRecalc={onRecalc}
                onDelete={onDelete}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function RowHeader({
  showTipo,
  sort,
  onSort,
}: {
  showTipo?: boolean
  sort: SortState
  onSort: (column: SortColumn) => void
}) {
  return (
    <div
      className={`text-muted-foreground border-b border-border bg-background grid gap-x-4 px-6 py-2.5 text-[10px] font-medium tracking-widest2 uppercase ${
        showTipo
          ? 'grid-cols-[48px_64px_92px_40px_72px_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_60px_128px]'
          : 'grid-cols-[48px_92px_40px_72px_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_60px_128px]'
      }`}
    >
      <SortHeader label="N°" column="numero" sort={sort} onSort={onSort} />
      {showTipo ? <span>Tipo</span> : null}
      <SortHeader label="Data" column="data" sort={sort} onSort={onSort} />
      <span>Dia</span>
      <span>Moeda</span>
      <span className="text-right">Valor original</span>
      <span className="text-right">USD</span>
      <span className="text-right">BRL</span>
      <span>Memória</span>
      <span className="text-right">Ações</span>
    </div>
  )
}

function SortHeader({
  label,
  column,
  sort,
  onSort,
}: {
  label: string
  column: SortColumn
  sort: SortState
  onSort: (column: SortColumn) => void
}) {
  const active = sort.column === column
  const Icon = active
    ? sort.direction === 'asc'
      ? ChevronUp
      : ChevronDown
    : ChevronsUpDown
  return (
    <button
      type="button"
      onClick={() => onSort(column)}
      aria-label={`Ordenar por ${label}`}
      aria-pressed={active}
      className={[
        'group flex items-center gap-1 transition-colors',
        active ? 'text-foreground' : 'hover:text-foreground',
      ].join(' ')}
    >
      <span>{label}</span>
      <Icon className={`h-3 w-3 ${active ? 'opacity-100' : 'opacity-40 group-hover:opacity-70'}`} />
    </button>
  )
}
